import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, ActionButton, CommonCss } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { showAlert } from 'utils/toastBridge';
import { acctClassOptions } from 'budget/budgetAcctStore';
import { listBudgetKindOptions } from 'budget/budgetRoundStore';
import { MONTH_STACK_COLUMNS } from 'budget/budgetMonthlyBudgetStore';
import {
  ALLOC_TYPE,
  ALLOC_TYPE_OPTIONS,
  ALLOC_PLAN_LABEL,
  ALLOC_PROCESS_LABEL,
  ALLOC_APPROVE_LABEL,
  getPlanMeta,
  saveAllocPlans,
  setPlanConfirm,
  buildPlanRowsForSearch,
  loadAllocProcesses,
  saveAllocProcesses,
  buildRegularProcessRows,
  buildAdhocProcessRows,
  applyProcessRequest,
  applyApprove,
  enrichAllocRow,
  createAllocUid,
  distributeMonthly,
  emptyMonthAmounts,
  withMonthlyGridIds,
} from 'budget/budgetAllocationStore';
import { filterExpenseRequests } from 'budget/budgetExpenseRequestStore';
import { filterIncomeRequests } from 'budget/budgetIncomeRequestStore';
import { loadDepts, filterDepts } from 'master/deptStore';
import { loadOrSeedProjects } from 'budget/budgetProjectStore';
import { loadSubjectsByFilter } from 'budget/budgetStructureStore';
import {
  buildSubjectDispCode,
  buildSubjectDispName,
} from 'budget/budgetSubjectHistoryStore';
import { loadFunds, loadFundPackages } from 'budget/budgetFundStore';

const AMT_SIZE = 96;

const MODE_META = {
  plan: { label: ALLOC_PLAN_LABEL, screenTitle: '예산배정계획수립' },
  process: { label: ALLOC_PROCESS_LABEL, screenTitle: '예산배정처리' },
  approve: { label: ALLOC_APPROVE_LABEL, screenTitle: '예산배정승인' },
};

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-top: 4px;
`;

const Hint = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
  flex: 1;
  min-width: 180px;
`;

const GridHost = styled.div`
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid ${theme.colors.border};
  border-radius: 4px;
  overflow: hidden;
`;

function formatCodeName(code, name) {
  const c = String(code || '').trim();
  const n = String(name || '').trim();
  if (c && n) return `${c} · ${n}`;
  return c || n || '';
}

function formatMultiCodeName(items) {
  const list = (items || [])
    .map((it) => formatCodeName(it.code, it.name))
    .filter(Boolean);
  if (!list.length) return '';
  if (list.length <= 2) return list.join(', ');
  return `${list.slice(0, 2).join(', ')} 외 ${list.length - 2}건`;
}

function joinCodes(values) {
  return (values || [])
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join(',');
}

function emptySearch(partial = {}) {
  return {
    fiscalYear: '',
    budgetKindCode: '',
    inoutDiv: '지출',
    allocType: ALLOC_TYPE.REGULAR,
    acctClass: '',
    deptCode: '',
    deptName: '',
    deptDisp: '',
    projectCode: '',
    projectName: '',
    projectDisp: '',
    subjectCode: '',
    subjectName: '',
    subjectDisp: '',
    fundUid: '',
    fundPkgUid: '',
    fundCode: '',
    fundName: '',
    fundDisp: '',
    ...partial,
  };
}

function allocTypeLabel(v) {
  return v === ALLOC_TYPE.ADHOC ? '수시' : '정기';
}

/**
 * 예산배정 공통 워크스페이스
 * - plan: 계획수립 (월별 배분 · 계획확정)
 * - process: 정기/수시 배정처리 · 배정요청
 * - approve: 요청건 승인
 */
export default function BudgetAllocationWorkspace({ screenMode = 'plan' }) {
  const mode = MODE_META[screenMode] ? screenMode : 'plan';
  const modeMeta = MODE_META[mode];
  const gridId = `budget-alloc-${mode}`;

  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const year = getCurrentFiscalYear();

  const gridRef = useRef(null);
  const allRowsRef = useRef([]);
  const lastDimsRef = useRef(null);

  const [warnMsg, setWarnMsg] = useState('');
  const [viewCount, setViewCount] = useState(0);
  const [planConfirmYn, setPlanConfirmYn] = useState('N');
  const [lookup, setLookup] = useState({ open: false, type: null });
  const [kindOptions, setKindOptions] = useState([]);

  const classOptions = useMemo(() => acctClassOptions(tenantCode), [tenantCode]);

  const searchDefaults = useMemo(
    () =>
      emptySearch({
        fiscalYear: year,
        budgetKindCode: '',
        inoutDiv: '지출',
        // 처리: 정기 기본 / 승인: 전체
        allocType: mode === 'approve' ? '' : ALLOC_TYPE.REGULAR,
      }),
    [year, mode]
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue, watch } = methods;
  const watchedFiscalYear = watch('fiscalYear');
  const watchedAllocType = watch('allocType');

  const grid = useHiGridHook({
    gridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: false,
  });

  useEffect(() => {
    const fy = watchedFiscalYear || year;
    const opts = listBudgetKindOptions(tenantCode, fy);
    setKindOptions(opts);
    const cur = getValues('budgetKindCode');
    if (cur && !opts.some((o) => o.value === cur)) {
      setValue('budgetKindCode', '');
    } else if (!cur && opts.length === 1) {
      setValue('budgetKindCode', opts[0].value);
    }
  }, [tenantCode, watchedFiscalYear, year, setValue, getValues]);

  /** 계획확정 여부·승인모드에 따른 화면 단위 편집 가능 */
  const screenCanEdit = useMemo(() => {
    if (mode === 'approve') return false;
    if (mode === 'plan') return planConfirmYn !== 'Y';
    return true;
  }, [mode, planConfirmYn]);

  /** 행 단위 월·절감 편집 (처리: 요청/승인 전만) */
  const rowCanEdit = useCallback(
    (row) => {
      if (mode === 'approve') return false;
      if (mode === 'plan') return planConfirmYn !== 'Y';
      const e = enrichAllocRow(row || {});
      return e.processYn !== 'Y' && e.approveYn !== 'Y';
    },
    [mode, planConfirmYn]
  );

  const refreshWarn = useCallback((list) => {
    const unallocCnt = (list || []).filter((r) => enrichAllocRow(r).hasUnalloc).length;
    setWarnMsg(unallocCnt ? `미배분액 ≠ 0 인 행 ${unallocCnt}건` : '');
  }, []);

  const syncGridIntoAllRows = useCallback(() => {
    const gridData = gridRef.current?.getData?.() || [];
    if (!gridData.length) return allRowsRef.current || [];
    const byId = new Map(
      gridData.map((r) => [String(r._rowUid || r.allocUid || r.requestUid || r.id || ''), r])
    );
    const next = (allRowsRef.current || []).map((r) => {
      const id = String(r._rowUid || r.allocUid || r.requestUid || r.id || '');
      const g = byId.get(id);
      return g ? { ...g } : r;
    });
    allRowsRef.current = next;
    return next;
  }, []);

  const readDims = useCallback(() => {
    const v = getValues();
    return {
      tenantCode: tenantCode || '',
      fiscalYear: String(v.fiscalYear || year || '').trim(),
      budgetKindCode: String(v.budgetKindCode || '').trim(),
      inoutDiv: String(v.inoutDiv || '지출').trim(),
      allocType:
        String(v.allocType || '').trim() === ALLOC_TYPE.ADHOC
          ? ALLOC_TYPE.ADHOC
          : ALLOC_TYPE.REGULAR,
      acctClass: String(v.acctClass || '').trim(),
    };
  }, [getValues, tenantCode, year]);

  const filterFromForm = useCallback(() => {
    const v = getValues();
    return {
      deptCode: String(v.deptCode || '').trim(),
      projectCode: String(v.projectCode || '').trim(),
      subjectCode: String(v.subjectCode || '').trim(),
      fundUid: String(v.fundUid || '').trim(),
      fundPkgUid: String(v.fundPkgUid || '').trim(),
      fundCode: String(v.fundCode || '').trim(),
    };
  }, [getValues]);

  const applyViewFilter = useCallback(
    (allRows, filterQ, dims) => {
      const q = filterQ || filterFromForm();
      const inoutDiv = String(dims?.inoutDiv || getValues('inoutDiv') || '지출').trim();
      const rowFilter = inoutDiv === '수입' ? filterIncomeRequests : filterExpenseRequests;
      let list = rowFilter(allRows || [], q);
      const acct = String(dims?.acctClass || getValues('acctClass') || '').trim();
      if (acct) {
        list = list.filter((r) => {
          const rowAcct = String(r.acctClassCode || r.acctClass || '').trim();
          return !rowAcct || rowAcct === acct;
        });
      }
      return list;
    },
    [filterFromForm, getValues]
  );

  const withDisplay = useCallback((r) => {
    const e = enrichAllocRow(r);
    return {
      ...e,
      allocTypeNm: allocTypeLabel(e.allocType),
      _sel: r._sel || 'N',
    };
  }, []);

  const pushViewToGrid = useCallback(
    (allRows, filterQ, dims) => {
      const view = withMonthlyGridIds(applyViewFilter(allRows, filterQ, dims).map(withDisplay));
      gridRef.current?.setData?.(view);
      setViewCount(view.length);
      refreshWarn(view);
      return view;
    },
    [applyViewFilter, refreshWarn, withDisplay]
  );

  const requireCoreDims = useCallback((dims) => {
    if (!dims.tenantCode || !dims.fiscalYear || !dims.budgetKindCode || !dims.inoutDiv) {
      showAlert('회계연도·예산차수·수지(지출/수입)를 선택하세요.', 'warning');
      return false;
    }
    return true;
  }, []);

  /** F7 조회 — 모드별 행 조립 후 검색조건 필터 */
  const handleSearch = useCallback(async () => {
    const dims = readDims();
    if (!requireCoreDims(dims)) return;

    let rows = [];
    if (mode === 'plan') {
      rows = await buildPlanRowsForSearch(dims);
      const meta = getPlanMeta(dims);
      setPlanConfirmYn(meta.planConfirmYn);
    } else if (mode === 'process') {
      if (dims.allocType === ALLOC_TYPE.REGULAR) {
        const result = await buildRegularProcessRows(dims);
        if (!result.ok) {
          showAlert(result.reason || '정기배정 조회에 실패했습니다.', 'warning');
          allRowsRef.current = [];
          lastDimsRef.current = dims;
          gridRef.current?.setData?.([]);
          setViewCount(0);
          refreshWarn([]);
          return;
        }
        rows = result.rows;
      } else {
        rows = await buildAdhocProcessRows(dims);
      }
      const meta = getPlanMeta(dims);
      setPlanConfirmYn(meta.planConfirmYn);
    } else {
      // 승인: 배정요청(processYn=Y) 건만
      rows = (await loadAllocProcesses(dims)).filter((r) => enrichAllocRow(r).processYn === 'Y');
      const allocFilter = String(getValues('allocType') || '').trim();
      if (allocFilter === ALLOC_TYPE.REGULAR || allocFilter === ALLOC_TYPE.ADHOC) {
        rows = rows.filter((r) => enrichAllocRow(r).allocType === allocFilter);
      }
    }

    const withSel = withMonthlyGridIds(
      (rows || []).map((r) => ({
        ...withDisplay(r),
        _sel: 'N',
        mod: r.mod || '',
      }))
    );
    allRowsRef.current = withSel;
    lastDimsRef.current = dims;
    const view = pushViewToGrid(withSel, filterFromForm(), dims);
    showAlert(`${modeMeta.label} ${view.length}건 조회 (전체 ${withSel.length}건)`, 'success');
  }, [
    mode,
    modeMeta.label,
    readDims,
    requireCoreDims,
    pushViewToGrid,
    filterFromForm,
    getValues,
    refreshWarn,
    withDisplay,
  ]);

  const columns = useMemo(() => {
    const selCol = hiCol.edCheckboxColumn({
      header: '선택',
      field: '_sel',
      size: 56,
      editable: () => true,
    });
    const base = [
      {
        ...selCol,
        enableSorting: false,
        enableColumnFilter: false,
        header: ({ table }) => {
          const rows =
            table.getFilteredRowModel?.()?.rows || table.getRowModel()?.rows || [];
          const isY = (r) => String(r?.original?._sel || '').toUpperCase() === 'Y';
          const allChecked = rows.length > 0 && rows.every(isY);
          const someChecked = !allChecked && rows.some(isY);
          return React.createElement('input', {
            type: 'checkbox',
            checked: allChecked,
            disabled: rows.length === 0,
            ref: (el) => {
              if (el) el.indeterminate = someChecked;
            },
            title: '전체 선택/해제',
            'aria-label': '전체 선택/해제',
            onMouseDown: (ev) => ev.stopPropagation(),
            onClick: (ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              if (!rows.length) return;
              const next = allChecked ? 'N' : 'Y';
              const visible = new Set(rows.map((r) => r.original).filter(Boolean));
              gridRef.current?.setData?.((prev) =>
                (prev || []).map((r) => (visible.has(r) ? { ...r, _sel: next } : r))
              );
            },
            style: { cursor: rows.length ? 'pointer' : 'default', margin: 0 },
          });
        },
      },
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 110 }),
      hiCol.roTextColumn({ header: '사업', field: 'projectName', size: 120 }),
      hiCol.roTextColumn({ header: '과목', field: 'subjectName', size: 120 }),
      hiCol.roTextColumn({ header: '재원', field: 'fundName', size: 90 }),
      hiCol.roTextColumn({
        header: '변경번호',
        field: 'changeNo',
        size: 80,
        align: 'center',
        title: '예산변경에서 배정까지 승인된 건',
      }),
    ];

    if (mode === 'process' || mode === 'approve') {
      base.push(
        hiCol.roTextColumn({
          header: '배정구분',
          field: 'allocTypeNm',
          size: 72,
          align: 'center',
        })
      );
      base.push(
        hiCol.roTextColumn({ header: '요청', field: 'processYn', size: 56, align: 'center' })
      );
    }
    if (mode === 'approve') {
      base.push(
        hiCol.roTextColumn({ header: '승인', field: 'approveYn', size: 56, align: 'center' })
      );
    }

    base.push(
      hiCol.roNumberColumn({ header: '예산액', field: 'budgetAmt', size: AMT_SIZE }),
      hiCol.edNumberColumn({
        header: '절감액',
        field: 'savingAmt',
        size: AMT_SIZE,
        editable: (row) => rowCanEdit(row),
      }),
      hiCol.roNumberColumn({ header: '배분기준액', field: 'baseAmt', size: AMT_SIZE }),
      ...MONTH_STACK_COLUMNS.map((c) =>
        hiCol.stackNumberColumn({
          topField: c.top,
          bottomField: c.bottom,
          topLabel: c.topLabel,
          bottomLabel: c.bottomLabel,
          size: 86,
          topEditable: c.kind === 'month',
          bottomEditable: c.kind === 'month',
          editable: c.kind === 'month' ? (row) => rowCanEdit(row) : false,
        })
      ),
      hiCol.roNumberColumn({ header: '합계', field: 'total', size: 88 }),
      hiCol.roNumberColumn({ header: '미배분', field: 'unalloc', size: 88 })
    );
    return base;
  }, [mode, rowCanEdit]);

  useEffect(() => {
    const derivedFields = new Set([
      'baseAmt',
      'q1',
      'q2',
      'q3',
      'q4',
      'total',
      'unalloc',
      'budgetAmt',
      'hasUnalloc',
      '_sel',
    ]);
    grid.onAfterSaveCell = (rowData, { field }) => {
      if (!field || derivedFields.has(field)) return;
      if (/^m\d+_m\d+$/i.test(String(field)) || /^q\d+_q\d+$/i.test(String(field))) return;
      if (!rowCanEdit(rowData)) return;
      const rowUid = rowData._rowUid || rowData.allocUid || rowData.id;
      if (rowUid == null || String(rowUid).trim() === '') return;
      const enriched = enrichAllocRow({
        ...rowData,
        mod: rowData.mod === 'A' ? 'A' : 'U',
      });
      gridRef.current?.setData?.((prev) => {
        const next = (prev || []).map((r) => {
          if (String(r._rowUid || r.allocUid || r.id || '') !== String(rowUid)) return r;
          return {
            ...enriched,
            _rowUid: rowUid,
            id: rowUid,
            _sel: r._sel || 'N',
          };
        });
        refreshWarn(next);
        return next;
      });
    };
  }, [grid, refreshWarn, rowCanEdit]);

  const applyDistribute = useCallback(
    (distMode) => {
      if (!screenCanEdit) return;
      gridRef.current?.stopEditCell?.();
      const data = gridRef.current?.getData?.() || [];
      const isSelected = (r) => String(r?._sel || '').toUpperCase() === 'Y';
      const targets = data.filter((r) => isSelected(r) && rowCanEdit(r));
      if (!targets.length) {
        showAlert('자동계산할 편집가능 행을 「선택」 체크하세요.', 'warning');
        return;
      }
      const modeLabel = { month: '월', quarter: '분기', half: '반기', year: '연' }[distMode] || distMode;
      const next = data.map((r) => {
        if (!isSelected(r) || !rowCanEdit(r)) return r;
        const rowUid = r._rowUid || r.allocUid || r.id;
        const base = enrichAllocRow(r);
        const months = distributeMonthly(base.baseAmt, distMode);
        return enrichAllocRow({
          ...base,
          ...emptyMonthAmounts(),
          ...months,
          _rowUid: rowUid,
          id: rowUid,
          _sel: 'Y',
          mod: r.mod === 'A' ? 'A' : 'U',
        });
      });
      gridRef.current?.setData?.(next);
      const byUid = new Map(
        next.map((r) => [String(r._rowUid || r.allocUid || r.requestUid || r.id || ''), r])
      );
      allRowsRef.current = (allRowsRef.current || []).map((r) => {
        const id = String(r._rowUid || r.allocUid || r.requestUid || r.id || '');
        return byUid.has(id) ? { ...byUid.get(id) } : r;
      });
      refreshWarn(next);
      const stillBad = next.filter(isSelected).filter((r) => enrichAllocRow(r).unalloc !== 0);
      if (stillBad.length) {
        showAlert(
          `선택 ${targets.length}건에 「${modeLabel}」 배분을 적용했지만, 미배분 ≠ 0 인 행이 ${stillBad.length}건 있습니다.`,
          'warning'
        );
      } else {
        showAlert(`선택 ${targets.length}건에 「${modeLabel}」 배분을 적용했습니다.`, 'success');
      }
    },
    [screenCanEdit, rowCanEdit, refreshWarn]
  );

  const selectedIds = useCallback(() => {
    const data = gridRef.current?.getData?.() || [];
    return data
      .filter((r) => String(r?._sel || '').toUpperCase() === 'Y')
      .map((r) => String(r._rowUid || r.allocUid || r.id || ''))
      .filter(Boolean);
  }, []);

  /** F10 저장 */
  const handleSave = useCallback(async () => {
    const dims = lastDimsRef.current || readDims();
    if (!requireCoreDims(dims)) return;

    if (mode === 'approve') {
      const all = syncGridIntoAllRows();
      const others = (await loadAllocProcesses(dims)).filter((r) => enrichAllocRow(r).processYn !== 'Y');
      const approvedView = all.map((r) => {
        const rest = { ...r };
        delete rest._sel;
        return enrichAllocRow(rest);
      });
      const prevRequested = (await loadAllocProcesses(dims)).filter((r) => enrichAllocRow(r).processYn === 'Y');
      const byUid = new Map(
        approvedView.map((r) => [String(r.allocUid || r._rowUid || ''), r])
      );
      const mergedRequested = prevRequested.map((r) => {
        const id = String(r.allocUid || '');
        return byUid.has(id) ? byUid.get(id) : r;
      });
      approvedView.forEach((r) => {
        const id = String(r.allocUid || '');
        if (id && !mergedRequested.some((x) => String(x.allocUid) === id)) {
          mergedRequested.push(r);
        }
      });
      const saved = await saveAllocProcesses(dims, [...others, ...mergedRequested]);
      const viewSource = saved.filter((r) => enrichAllocRow(r).processYn === 'Y');
      const withSel = withMonthlyGridIds(
        viewSource.map((r) => ({ ...r, _sel: 'N', mod: '' }))
      );
      allRowsRef.current = withSel;
      pushViewToGrid(withSel, filterFromForm(), dims);
      showAlert('승인 내역을 저장했습니다.', 'success');
      return;
    }

    if (mode === 'plan') {
      if (planConfirmYn === 'Y') {
        showAlert('계획이 확정되어 저장할 수 없습니다. 확정취소 후 수정하세요.', 'warning');
        return;
      }
      const all = syncGridIntoAllRows();
      const data = all.map((r) => {
        const rest = { ...r };
        delete rest._sel;
        delete rest.mod;
        return enrichAllocRow(rest);
      });
      const saved = await saveAllocPlans(dims, data);
      const withSel = withMonthlyGridIds(saved.map((r) => ({ ...r, _sel: 'N', mod: '' })));
      allRowsRef.current = withSel;
      pushViewToGrid(withSel, filterFromForm(), dims);
      const unallocCnt = withSel.filter((r) => r.hasUnalloc).length;
      showAlert(
        unallocCnt
          ? `배정계획을 저장했습니다. 미배분 행 ${unallocCnt}건`
          : '배정계획을 저장했습니다.',
        unallocCnt ? 'warning' : 'success'
      );
      return;
    }

    // process: 현재 allocType만 교체, 다른 유형 유지
    const all = syncGridIntoAllRows();
    const currentType = dims.allocType;
    const others = (await loadAllocProcesses(dims)).filter((r) => enrichAllocRow(r).allocType !== currentType);
    const current = all.map((r) => {
      const rest = { ...r };
      delete rest._sel;
      return enrichAllocRow({ ...rest, allocType: currentType });
    });
    const saved = await saveAllocProcesses(dims, [...others, ...current]);
    const viewSource = saved.filter((r) => enrichAllocRow(r).allocType === currentType);
    const withSel = withMonthlyGridIds(viewSource.map((r) => ({ ...r, _sel: 'N', mod: '' })));
    allRowsRef.current = withSel;
    pushViewToGrid(withSel, filterFromForm(), dims);
    showAlert('배정처리를 저장했습니다.', 'success');
  }, [
    mode,
    planConfirmYn,
    readDims,
    requireCoreDims,
    syncGridIntoAllRows,
    pushViewToGrid,
    filterFromForm,
  ]);

  /** 계획확정 / 확정취소 */
  const handlePlanConfirmToggle = useCallback(async () => {
    const dims = lastDimsRef.current || readDims();
    if (!requireCoreDims(dims)) return;
    syncGridIntoAllRows();

    if (planConfirmYn !== 'Y') {
      const list = allRowsRef.current || [];
      const bad = list.filter((r) => enrichAllocRow(r).hasUnalloc);
      if (bad.length) {
        showAlert(`미배분 행 ${bad.length}건이 있어 계획확정할 수 없습니다.`, 'warning');
        return;
      }
      if (!list.length) {
        showAlert('확정할 계획 행이 없습니다. 먼저 조회·저장하세요.', 'warning');
        return;
      }
      await saveAllocPlans(
        dims,
        list.map((r) => {
          const rest = { ...r };
          delete rest._sel;
          delete rest.mod;
          return rest;
        })
      );
      const meta = await setPlanConfirm(dims, 'Y', user?.id || user?.name || '');
      setPlanConfirmYn(meta.planConfirmYn);
      showAlert('배정계획을 확정했습니다.', 'success');
      await handleSearch();
      return;
    }

    const meta = await setPlanConfirm(dims, 'N', '');
    setPlanConfirmYn(meta.planConfirmYn);
    showAlert('계획확정을 취소했습니다.', 'success');
    await handleSearch();
  }, [planConfirmYn, readDims, requireCoreDims, syncGridIntoAllRows, user, handleSearch]);

  /** 배정요청 / 요청취소 */
  const handleProcessRequest = useCallback(
    async (yn) => {
      const ids = selectedIds();
      if (!ids.length) {
        showAlert(`${yn === 'Y' ? '배정요청' : '요청취소'}할 행을 선택하세요.`, 'warning');
        return;
      }
      gridRef.current?.stopEditCell?.();
      const data = gridRef.current?.getData?.() || [];
      if (yn === 'Y') {
        const skipped = data.filter(
          (r) =>
            String(r?._sel || '').toUpperCase() === 'Y' && enrichAllocRow(r).hasUnalloc
        );
        if (skipped.length) {
          showAlert(
            `미배분 행 ${skipped.length}건은 요청에서 제외됩니다. 배분을 완료하세요.`,
            'warning'
          );
        }
      }
      const next = applyProcessRequest(data, ids, user?.id || user?.name || '', yn);
      gridRef.current?.setData?.(next);
      const byUid = new Map(
        next.map((r) => [String(r._rowUid || r.allocUid || r.id || ''), r])
      );
      allRowsRef.current = (allRowsRef.current || []).map((r) => {
        const id = String(r._rowUid || r.allocUid || r.id || '');
        return byUid.has(id) ? { ...byUid.get(id) } : r;
      });
      refreshWarn(next);
      // 요청 즉시 저장
      const dims = lastDimsRef.current || readDims();
      if (dims?.budgetKindCode) {
        const all = allRowsRef.current;
        const currentType = dims.allocType || ALLOC_TYPE.REGULAR;
        const others = (await loadAllocProcesses(dims)).filter(
          (r) => enrichAllocRow(r).allocType !== currentType
        );
        const current = all.map((r) => {
          const rest = { ...r };
          delete rest._sel;
          return enrichAllocRow({ ...rest, allocType: currentType });
        });
        await saveAllocProcesses(dims, [...others, ...current]);
      }
      showAlert(yn === 'Y' ? '배정요청을 반영했습니다.' : '요청취소를 반영했습니다.', 'success');
    },
    [selectedIds, user, refreshWarn, readDims]
  );

  /** 승인 / 승인취소 */
  const handleApprove = useCallback(
    async (yn) => {
      const ids = selectedIds();
      if (!ids.length) {
        showAlert(`${yn === 'Y' ? '승인' : '승인취소'}할 행을 선택하세요.`, 'warning');
        return;
      }
      const data = gridRef.current?.getData?.() || [];
      const eligible = data.filter(
        (r) =>
          String(r?._sel || '').toUpperCase() === 'Y' &&
          enrichAllocRow(r).processYn === 'Y'
      );
      if (!eligible.length) {
        showAlert('배정요청(요청=Y)된 행만 승인할 수 있습니다.', 'warning');
        return;
      }
      const next = applyApprove(data, ids, user?.id || user?.name || '', yn);
      gridRef.current?.setData?.(next);
      const byUid = new Map(
        next.map((r) => [String(r._rowUid || r.allocUid || r.id || ''), r])
      );
      allRowsRef.current = (allRowsRef.current || []).map((r) => {
        const id = String(r._rowUid || r.allocUid || r.id || '');
        return byUid.has(id) ? { ...byUid.get(id) } : r;
      });
      // 승인 즉시 저장
      const dims = lastDimsRef.current || readDims();
      if (dims?.budgetKindCode) {
        const all = allRowsRef.current;
        const others = (await loadAllocProcesses(dims)).filter((r) => enrichAllocRow(r).processYn !== 'Y');
        const requested = all.map((r) => {
          const rest = { ...r };
          delete rest._sel;
          return enrichAllocRow(rest);
        });
        const prevRequested = (await loadAllocProcesses(dims)).filter(
          (r) => enrichAllocRow(r).processYn === 'Y'
        );
        const map = new Map(requested.map((r) => [String(r.allocUid || ''), r]));
        const mergedRequested = prevRequested.map((r) => map.get(String(r.allocUid || '')) || r);
        await saveAllocProcesses(dims, [...others, ...mergedRequested]);
      }
      showAlert(yn === 'Y' ? '승인했습니다.' : '승인취소했습니다.', 'success');
    },
    [selectedIds, user, readDims]
  );

  /** 수시배정 행 추가 (F8) */
  const handleAdd = useCallback(() => {
    if (mode !== 'process') return;
    const dims = lastDimsRef.current || readDims();
    if (dims.allocType !== ALLOC_TYPE.ADHOC && getValues('allocType') !== ALLOC_TYPE.ADHOC) {
      showAlert('정기배정은 행을 추가할 수 없습니다. 수시배정으로 조회 후 추가하세요.', 'warning');
      return;
    }
    const uid = createAllocUid();
    const row = enrichAllocRow({
      ...emptyMonthAmounts(),
      allocUid: uid,
      _rowUid: uid,
      id: uid,
      allocType: ALLOC_TYPE.ADHOC,
      budgetAmt: 0,
      savingAmt: 0,
      processYn: 'N',
      approveYn: 'N',
      _sel: 'N',
      mod: 'A',
      deptCode: '',
      deptName: '',
      projectCode: '',
      projectName: '',
      subjectCode: '',
      subjectName: '',
      fundUid: '',
      fundCode: '',
      fundName: '',
    });
    const next = withMonthlyGridIds([...(gridRef.current?.getData?.() || []), row]);
    gridRef.current?.setData?.(next);
    allRowsRef.current = [...(allRowsRef.current || []), row];
    setViewCount(next.length);
    showAlert('수시배정 행을 추가했습니다. 부서·사업·과목·재원·금액을 입력 후 저장하세요.', 'success');
  }, [mode, readDims, getValues]);

  const openSearchLookup = useCallback((type) => {
    setLookup({ open: true, type });
  }, []);

  const clearSearchCodeFields = useCallback(
    (kind) => {
      if (kind === 'dept') {
        setValue('deptCode', '');
        setValue('deptName', '');
        setValue('deptDisp', '');
      } else if (kind === 'project') {
        setValue('projectCode', '');
        setValue('projectName', '');
        setValue('projectDisp', '');
      } else if (kind === 'subject') {
        setValue('subjectCode', '');
        setValue('subjectName', '');
        setValue('subjectDisp', '');
      } else if (kind === 'fund') {
        setValue('fundUid', '');
        setValue('fundPkgUid', '');
        setValue('fundCode', '');
        setValue('fundName', '');
        setValue('fundDisp', '');
      }
    },
    [setValue]
  );

  const searchFields = useMemo(() => {
    const row1 = [
      {
        name: 'fiscalYear',
        label: '회계연도',
        type: 'fiscalYear',
        width: CommonCss.w100,
        labelWidth: SEARCH_LABEL_W,
        required: true,
      },
      {
        name: 'budgetKindCode',
        label: '예산차수',
        type: 'select',
        width: CommonCss.w160,
        labelWidth: SEARCH_LABEL_W,
        options: [{ value: '', label: '선택' }, ...kindOptions],
        required: true,
      },
      {
        name: 'inoutDiv',
        label: '수지',
        type: 'select',
        width: CommonCss.w90,
        labelWidth: SEARCH_LABEL_W,
        options: [
          { value: '지출', label: '지출' },
          { value: '수입', label: '수입' },
        ],
        required: true,
      },
    ];
    if (mode === 'process') {
      row1.push({
        name: 'allocType',
        label: '배정구분',
        type: 'select',
        width: CommonCss.w160,
        labelWidth: SEARCH_LABEL_W,
        options: ALLOC_TYPE_OPTIONS,
      });
    }
    if (mode === 'approve') {
      row1.push({
        name: 'allocType',
        label: '배정구분',
        type: 'select',
        width: CommonCss.w160,
        labelWidth: SEARCH_LABEL_W,
        options: [{ value: '', label: '전체' }, ...ALLOC_TYPE_OPTIONS],
      });
    }

    const row2 = [
      {
        name: 'acctClass',
        label: '회계구분',
        type: 'select',
        width: CommonCss.w110,
        labelWidth: SEARCH_LABEL_W,
        options: [{ value: '', label: '전체' }, ...classOptions],
      },
      {
        name: 'deptDisp',
        label: '부서',
        type: 'text',
        width: CommonCss.w160,
        labelWidth: SEARCH_LABEL_W,
        readonly: true,
        showSearch: true,
        searchTitle: '부서 조회',
        onSearch: () => openSearchLookup('dept'),
        placeholder: '전체',
      },
      {
        name: 'projectDisp',
        label: '사업',
        type: 'text',
        width: CommonCss.w180,
        labelWidth: SEARCH_LABEL_W,
        readonly: true,
        showSearch: true,
        searchTitle: '사업 조회',
        onSearch: () => openSearchLookup('project'),
        placeholder: '전체',
      },
      {
        name: 'subjectDisp',
        label: '과목',
        type: 'text',
        width: CommonCss.w180,
        labelWidth: SEARCH_LABEL_W,
        readonly: true,
        showSearch: true,
        searchTitle: '과목 조회',
        onSearch: () => openSearchLookup('subject'),
        placeholder: '전체',
      },
      {
        name: 'fundDisp',
        label: '재원',
        type: 'text',
        width: CommonCss.w160,
        labelWidth: SEARCH_LABEL_W,
        readonly: true,
        showSearch: true,
        searchTitle: '재원 조회',
        onSearch: () => openSearchLookup('fund'),
        placeholder: '전체',
      },
    ];
    return [row1, row2];
  }, [mode, kindOptions, classOptions, openSearchLookup]);

  const lookupConfig = useMemo(() => {
    const dims = lastDimsRef.current || readDims();
    const tenant = dims.tenantCode || tenantCode;
    if (lookup.type === 'dept') {
      const rows = filterDepts(loadDepts(tenant), { useYn: 'Y' }).map((r) => ({
        ...r,
        id: r.deptUid || r.deptCode,
        code: r.deptCode,
        name: r.deptName,
      }));
      return {
        title: '부서 조회',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 110 }),
          hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 160 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: ['deptCode', 'deptName', 'code', 'name'],
        },
      };
    }
    if (lookup.type === 'project') {
      const list = loadOrSeedProjects(dims) || [];
      const rows = list
        .filter((r) => r.useYn !== 'N')
        .map((r) => ({
          ...r,
          mod: '',
          id: r.projectUid || r.projectCode,
          code: r.projectCode,
          name: r.projectName,
        }));
      return {
        title: '사업 조회',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 110 }),
          hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 180 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: ['projectCode', 'projectName', 'code', 'name'],
        },
      };
    }
    if (lookup.type === 'subject') {
      const all = loadSubjectsByFilter({
        ...dims,
        inoutDiv: dims.inoutDiv || '지출',
      });
      const finals = (all || []).filter((r) => r.finalYn === 'Y' && r.useYn !== 'N' && r.mod !== 'D');
      const rows = finals.map((r) => {
        const disp = buildSubjectDispCode(r, all);
        const dispName = buildSubjectDispName(r, all);
        return {
          ...r,
          id: r.subjectUid || `${r.budgetType}-${r.subjectCode}`,
          code: disp,
          name: dispName,
          subjectDispCode: disp,
          subjectDispName: dispName,
          subjectName: dispName,
        };
      });
      return {
        title: '과목 조회',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '결합코드', field: 'subjectDispCode', size: 140 }),
          hiCol.roTextColumn({ header: '과목명', field: 'subjectDispName', size: 260 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: [
            'subjectDispCode',
            'subjectCode',
            'subjectDispName',
            'subjectName',
            'code',
            'name',
          ],
        },
      };
    }
    if (lookup.type === 'fund') {
      const fundRows = loadFunds(dims)
        .filter((r) => r.useYn !== 'N')
        .map((r) => ({
          ...r,
          id: r.fundUid || r.fundCode,
          code: r.fundCode,
          name: r.fundName,
          _kind: 'fund',
        }));
      const fundCols = [
        hiCol.roTextColumn({ header: '재원코드', field: 'fundCode', size: 100 }),
        hiCol.roTextColumn({ header: '재원명', field: 'fundName', size: 140 }),
      ];
      const pkgRows = loadFundPackages(dims)
        .filter((r) => r.useYn !== 'N')
        .map((r) => ({
          ...r,
          id: r.fundPkgUid || r.pkgCode,
          code: r.pkgCode,
          name: r.pkgName,
          _kind: 'package',
        }));
      return {
        title: '재원·재원구성 조회',
        rows: fundRows,
        columns: fundCols,
        tabs: [
          { id: 'fund', label: '재원', rows: fundRows, columns: fundCols },
          {
            id: 'package',
            label: '재원구성',
            rows: pkgRows,
            columns: [
              hiCol.roTextColumn({ header: '구성코드', field: 'pkgCode', size: 110 }),
              hiCol.roTextColumn({ header: '구성명', field: 'pkgName', size: 160 }),
            ],
          },
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: ['fundCode', 'fundName', 'pkgCode', 'pkgName', 'code', 'name'],
        },
      };
    }
    return { title: '코드 조회', rows: [], columns: [] };
  }, [lookup.type, tenantCode, readDims]);

  const applyLookup = useCallback(
    (payload) => {
      const rows = (Array.isArray(payload) ? payload : [payload]).filter(Boolean);
      if (!rows.length) {
        if (lookup.type === 'dept') clearSearchCodeFields('dept');
        else if (lookup.type === 'project') clearSearchCodeFields('project');
        else if (lookup.type === 'subject') clearSearchCodeFields('subject');
        else if (lookup.type === 'fund') clearSearchCodeFields('fund');
        setLookup({ open: false, type: null });
        return;
      }
      if (lookup.type === 'dept') {
        const items = rows
          .map((r) => ({
            code: String(r.deptCode || '').trim(),
            name: String(r.deptName || '').trim(),
          }))
          .filter((x) => x.code);
        setValue('deptCode', joinCodes(items.map((x) => x.code)));
        setValue('deptName', items.map((x) => x.name).filter(Boolean).join(', '));
        setValue('deptDisp', formatMultiCodeName(items));
      } else if (lookup.type === 'project') {
        const items = rows
          .map((r) => ({
            code: String(r.projectCode || '').trim(),
            name: String(r.projectName || '').trim(),
          }))
          .filter((x) => x.code);
        setValue('projectCode', joinCodes(items.map((x) => x.code)));
        setValue('projectName', items.map((x) => x.name).filter(Boolean).join(', '));
        setValue('projectDisp', formatMultiCodeName(items));
      } else if (lookup.type === 'subject') {
        const dims = lastDimsRef.current || readDims();
        const all = loadSubjectsByFilter({
          ...dims,
          inoutDiv: dims.inoutDiv || '지출',
        });
        const items = rows
          .map((r) => {
            const code =
              r.subjectDispCode || buildSubjectDispCode(r, all) || r.subjectCode || '';
            const name =
              r.subjectDispName || r.subjectName || buildSubjectDispName(r, all) || '';
            const leaf = String(r.subjectCode || code).trim();
            return { code: String(code).trim(), name: String(name).trim(), leaf };
          })
          .filter((x) => x.code);
        setValue(
          'subjectCode',
          joinCodes(items.flatMap((x) => [x.leaf, x.code].filter(Boolean)))
        );
        setValue('subjectName', items.map((x) => x.name).filter(Boolean).join(', '));
        setValue('subjectDisp', formatMultiCodeName(items));
      } else if (lookup.type === 'fund') {
        const fundItems = [];
        const pkgItems = [];
        for (const r of rows) {
          const isPkg = r._lookupTab === 'package' || r._kind === 'package' || r.fundPkgUid;
          if (isPkg && (r.fundPkgUid || r.pkgCode)) {
            const code = String(r.pkgCode || '').trim();
            if (!code) continue;
            pkgItems.push({
              uid: String(r.fundPkgUid || '').trim(),
              code,
              name: String(r.pkgName || '').trim(),
            });
          } else {
            const code = String(r.fundCode || '').trim();
            if (!code) continue;
            fundItems.push({
              uid: String(r.fundUid || '').trim(),
              code,
              name: String(r.fundName || '').trim(),
            });
          }
        }
        setValue(
          'fundUid',
          joinCodes(fundItems.flatMap((x) => [x.uid, x.code].filter(Boolean)))
        );
        setValue(
          'fundPkgUid',
          joinCodes(pkgItems.flatMap((x) => [x.uid, x.code].filter(Boolean)))
        );
        const dispItems = [
          ...fundItems.map((x) => ({ code: x.code, name: x.name })),
          ...pkgItems.map((x) => ({ code: x.code, name: x.name })),
        ];
        setValue('fundCode', joinCodes(dispItems.map((x) => x.code)));
        setValue('fundName', dispItems.map((x) => x.name).filter(Boolean).join(', '));
        setValue('fundDisp', formatMultiCodeName(dispItems));
      }
      setLookup({ open: false, type: null });
    },
    [lookup.type, clearSearchCodeFields, setValue, readDims]
  );

  const actionButtons = useMemo(() => {
    const buttons = [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
    ];
    if (mode === 'process' && watchedAllocType === ALLOC_TYPE.ADHOC) {
      buttons.push({
        id: 'add',
        label: '추가',
        icon: '➕',
        onClick: handleAdd,
        hotkey: 'F8',
        title: '수시배정 행 추가',
      });
    }
    buttons.push({
      id: 'save',
      label: '저장',
      icon: '✅',
      onClick: handleSave,
      hotkey: 'F10',
      variant: 'success',
      disabled: mode === 'plan' && planConfirmYn === 'Y',
    });
    if (mode === 'plan') {
      buttons.push({
        id: 'planConfirm',
        label: planConfirmYn === 'Y' ? '확정취소' : '계획확정',
        onClick: handlePlanConfirmToggle,
        title:
          planConfirmYn === 'Y'
            ? '계획확정을 취소합니다'
            : '미배분 0인 계획을 확정합니다 (확정 후 편집 불가)',
      });
    }
    if (mode === 'process') {
      buttons.push(
        {
          id: 'processReq',
          label: '배정요청',
          onClick: () => handleProcessRequest('Y'),
          title: '선택 행 배정요청 (미배분 행 제외)',
        },
        {
          id: 'processCancel',
          label: '요청취소',
          onClick: () => handleProcessRequest('N'),
        }
      );
    }
    if (mode === 'approve') {
      buttons.push(
        {
          id: 'approve',
          label: '승인',
          onClick: () => handleApprove('Y'),
          title: '선택 행 승인 (요청=Y만)',
        },
        {
          id: 'approveCancel',
          label: '승인취소',
          onClick: () => handleApprove('N'),
        }
      );
    }
    return buttons;
  }, [
    mode,
    watchedAllocType,
    planConfirmYn,
    handleSearch,
    handleAdd,
    handleSave,
    handlePlanConfirmToggle,
    handleProcessRequest,
    handleApprove,
  ]);

  const showDistribute = mode === 'plan' || mode === 'process';
  const planHint =
    mode === 'plan'
      ? `계획확정: ${planConfirmYn === 'Y' ? '확정' : '미확정'}`
      : mode === 'process'
        ? `계획확정: ${planConfirmYn === 'Y' ? '확정' : '미확정'} · 배정구분: ${allocTypeLabel(
            watchedAllocType
          )}`
        : '배정요청(요청=Y) 건만 표시 · 월별 금액은 읽기전용';

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} loading={grid.loading} />
        <CollapsibleSearchPanel
          storageKey={`budget-alloc-${mode}`}
          prefsUserId={prefsUserId}
          summary={`${modeMeta.screenTitle} · ${watchedFiscalYear || year}`}
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Toolbar>
        {showDistribute && (
          <>
            <ActionButton
              type="button"
              onClick={() => applyDistribute('month')}
              disabled={!screenCanEdit}
            >
              월
            </ActionButton>
            <ActionButton
              type="button"
              onClick={() => applyDistribute('quarter')}
              disabled={!screenCanEdit}
            >
              분기
            </ActionButton>
            <ActionButton
              type="button"
              onClick={() => applyDistribute('half')}
              disabled={!screenCanEdit}
            >
              반기
            </ActionButton>
            <ActionButton
              type="button"
              onClick={() => applyDistribute('year')}
              disabled={!screenCanEdit}
            >
              연
            </ActionButton>
          </>
        )}
        <Hint>
          {planHint}
          {showDistribute ? ' · 체크한 행만 일괄 배분' : ''}
          {` · 표시 ${viewCount}건 / 전체 ${allRowsRef.current?.length || 0}건`}
          {warnMsg ? ` · ${warnMsg}` : ''}
        </Hint>
      </Toolbar>

      <GridHost>
        <HiGridComponent
          ref={gridRef}
          columns={columns}
          height="100%"
          showRowNumber
          rowNumberSize={56}
          showModColumn={mode !== 'approve'}
          enableRowCheck={false}
          rowHeight={52}
          useVirtualRows={false}
          {...(mode !== 'approve' ? grid.contextMenuProps : {})}
          enableRowEditMenu={false}
          enableHeaderMenu
          enableColumnFilters
          enableFreeze
          enableSelectionStats
          enableFindReplace
          allowReplace={screenCanEdit}
          enableQuickExport
          exportFileName={modeMeta.label}
          gridId={gridId}
          prefsUserId={prefsUserId}
          colOptions={grid.colOptions}
        />
      </GridHost>

      <CodeLookupDialog
        open={lookup.open}
        title={lookupConfig.title}
        columns={lookupConfig.columns}
        rows={lookupConfig.rows}
        tabs={lookupConfig.tabs}
        enableSearch={!!lookupConfig.enableSearch}
        searchConfig={lookupConfig.searchConfig}
        multiSelect
        prefsUserId={prefsUserId}
        sizeKey={lookup.type ? `codeLookup-${lookup.type}` : 'codeLookup'}
        onSelect={applyLookup}
        onClose={() => setLookup({ open: false, type: null })}
      />
    </PageContainer>
  );
}
